import { INavLink } from "core-integration/src/hooks/types";
import useDocumentEventHandler from "core-integration/src/hooks/useDocumentEventHandler";
import { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getServerError } from "core-integration/src/store/global/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import BurgerSvg from "../../img/icons/burger.svg?react";
import CloseSvg from "../../img/icons/close-icon.svg?react";
import ListItem from "./ListItem";

const StyledNav = styled.nav`
  white-space: nowrap;
`;

const StyledUL = styled.ul`
  display: flex;
  justify-content: flex-end;
  padding: 0;
  font-size: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    justify-content: flex-start;
  }
`;

const DesktopMenuItems = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    display: inline-flex;
  }
`;

const MobileMenuContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    display: none;
  }
`;

const BurgerButton = styled.button<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.space[2]};
  border: ${({ theme }) => theme.borders[0]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all 0.2s;

  @media (min-width: ${({ theme }) => theme.breakpoints[5]}) {
    padding: 0 ${({ theme }) => theme.space[4]};
  }
`;

const StyledBurgerIcon = styled(BurgerSvg)`
  width: 24px;
  height: 24px;
  color: currentColor;
`;

const StyledCloseIcon = styled(CloseSvg)`
  width: 24px;
  height: 24px;
  color: currentColor;
`;

const MobileMenuOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: var(--header-height, 83px);
  left: 0;
  z-index: ${({ theme }) => theme.zIndex.overlay};
  width: 100%;
  height: calc(100% - var(--header-height));
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${({ isOpen }) => (isOpen ? "1" : "0")};
  pointer-events: ${({ isOpen }) => (isOpen ? "auto" : "none")};
  transition: opacity 0.2s;
`;

const MobileMenu = styled.ul<{ isOpen: boolean }>`
  position: fixed;
  top: var(--header-height, 83px);
  right: 1rem;
  z-index: ${({ theme }) => theme.zIndex.dialog};
  width: calc(100vw - 2rem);
  max-width: 20rem;
  max-height: calc(100vh - var(--header-height, 83px) - 1rem);
  padding-left: 0;
  border: ${({ theme }) => theme.borders[0]};
  border-top: none;
  border-radius: ${({ theme }) => `0 0 ${theme.radii[2]} ${theme.radii[2]}`};
  background-color: ${({ theme }) => theme.colors.mobileNavBg};
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  pointer-events: ${({ isOpen }) => (isOpen ? "auto" : "none")};
  transform: ${({ isOpen }) => (isOpen ? "scale(1)" : "scale(0)")};
  transform-origin: top right;
  transition: 0.1s;
`;

const NavLink = css`
  display: block;
  padding: 0 1.5rem;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  font-weight: 700;
  line-height: 4.7rem;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
`;

const InternalNavLink = styled(Link)`
  ${NavLink}
`;

const ExternalNavLink = styled.a`
  ${NavLink}
`;

const NavigationLinks: React.FC<{ links: INavLink[] }> = ({ links }) => {
  const { t } = useTranslation();
  const [burgerOpen, setBurgerOpen] = React.useState(false);
  useDocumentEventHandler({
    event: "click",
    handler: () => setBurgerOpen(false),
  });

  const toggleBurger = (e: React.SyntheticEvent) => {
    e.nativeEvent.stopImmediatePropagation();
    setBurgerOpen((open) => !open);
  };

  return (
    <StyledNav role="navigation" className="ism-nav">
      <StyledUL>
        {/* Full menu - hidden on mobile */}
        <DesktopMenuItems>
          {links.map((link) => (
            <ListItem {...link} key={link.text} />
          ))}
        </DesktopMenuItems>

        {/* Burger menu - hidden on desktop */}
        <MobileMenuContainer>
          <BurgerButton
            isOpen={burgerOpen}
            aria-controls="burger-menu"
            aria-label={t("navigation.more", "More")}
            type="button"
            onClick={toggleBurger}
          >
            {burgerOpen ? (
              <StyledCloseIcon aria-hidden="true" focusable="false" />
            ) : (
              <StyledBurgerIcon aria-hidden="true" focusable="false" />
            )}
          </BurgerButton>
          <MobileMenuOverlay isOpen={burgerOpen} />
          <MobileMenu isOpen={burgerOpen} id="burger-menu">
            {links.map((link) => (
              <li key={link.text}>
                {link.useRouter ? (
                  <InternalNavLink to={link.href}>{link.text}</InternalNavLink>
                ) : (
                  <ExternalNavLink
                    href={link.href}
                    rel={link.rel}
                    target={link.target}
                  >
                    {link.text}
                  </ExternalNavLink>
                )}
              </li>
            ))}
          </MobileMenu>
        </MobileMenuContainer>
      </StyledUL>
    </StyledNav>
  );
};

const mapStateToProps = (state: RootState) => {
  const player = getPlayerData(state);
  const currentEvent = getCurrentEvent(state);
  const nextEvent = getNextEvent(state);
  const serverError = getServerError(state);
  const entry = player && player.entry ? getEntry(state, player.entry) : null;

  return {
    player,
    currentEvent,
    nextEvent,
    serverError,
    entry,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Navigation: React.FC<PropsFromRedux> = ({
  player,
  currentEvent,
  nextEvent,
  serverError,
  entry,
}) => {
  const { t } = useTranslation();
  let links: INavLink[] = [];
  const useRouter = !serverError;
  // const allsvenskanLink = {
  //   useRouter: false,
  //   href: "//allsvenskan.se",
  //   text: "allsvenskan.se",
  //   isDraft: true
  // };
  if (player && player.entry) {
    // Logged in with entry
    links = [
      {
        useRouter,
        href: "/",
        text: currentEvent
          ? t("navigation.status", "Status")
          : t("navigation.home", "Home"),
      },
    ];
    if (nextEvent) {
      links.push({
        useRouter,
        href: "/my-team",
        text: t("navigation.myTeam", "My Team"),
      });
    }
    // If the entry has started ...
    if (currentEvent && entry && entry.started_event <= currentEvent.id) {
      links.push({
        useRouter,
        href: `/entry/${player.entry}/event/${currentEvent.id}`,
        text: t("navigation.points", "Points"),
      });
    }
    links.push(
      {
        useRouter,
        href: "/transfers",
        text: t("navigation.transfers", "Transfers"),
      },
      {
        useRouter,
        href: "/leagues",
        text: t("navigation.leagues", "Leagues"),
      },
      {
        useRouter,
        href: "/fixtures",
        text: t("navigation.fixtures", "Fixtures"),
      },
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.statistics", "Statistics"),
      },
      {
        useRouter,
        href: "/xstats",
        text: t("navigation.xStats", "XStats"),
      },
      { useRouter, href: "/the-scout", text: t("navigation.scout", "Scout") },
      {
        useRouter,
        href: "/prizes",
        text: t("navigation.prizes", "Prizes"),
      },
      { useRouter, href: "/help", text: t("navigation.help", "Help") },
      {
        useRouter: false,
        href: "api/player/logout_oidc",
        text: t("navigation.signOut", "Sign out"),
      },
    );
  } else if (player) {
    // Logged in, no entry
    links = [
      { useRouter, href: "/", text: t("navigation.home", "Home") },
      {
        useRouter,
        href: "/squad-selection",
        text: t("navigation.squadSelection", "Squad Selection"),
      },
      { useRouter, href: "/help", text: t("navigation.help", "Help") },
      {
        useRouter: false,
        href: "api/player/logout_oidc",
        text: t("navigation.signOut", "Sign out"),
      },
    ];
  } else {
    // Logged Out
    links = [
      { useRouter, href: "/", text: t("navigation.home", "Home") },
      {
        useRouter,
        href: "/prizes",
        text: t("navigation.prizes", "Prizes"),
      },
      { useRouter, href: "/the-scout", text: t("navigation.scout", "Scout") },
      { useRouter, href: "/help", text: t("navigation.help", "Help") },
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.statistics", "Statistics"),
      },
      // allsvenskanLink
    ];
  }
  return <NavigationLinks links={links} />;
};

export { Navigation as NavigationTest };

export default connector(Navigation);
